import {
    FullContactModel,
    LeadConfigurationModel,
    NoteModel,
    PackageModel,
    QuoteBelfiusModel,
    QuoteModel,
    QuotePdfGenerationModel,
    QuoteSimulationModel
} from "./Lead-configuration.model";
import {cast, flow, onSnapshot, types} from "mobx-state-tree";
import * as React from "react";
import {SolarSelectionModel} from "./solar-selection.model";
import {LeadGenerationOptionsModel} from "./leadgeneration-options.model";
import makeInspectable from 'mobx-devtools-mst';
import {izenApiService, MyIzenApiService} from "../services/my-izen-api.service";
import {MyIzenFlowService} from "../services/FlowService";
import {LeadGenerationOptions, PdfResponse} from "../services/types/lead-configuration-option.types";
import {BuildingTypeEnum, CreateLeadDto, TypeEnum} from "../services/types/lead-generation-create.types";
import {LeadGenerationStep} from "../services/types/lead-generation-step";
import {debounce} from "lodash";
import {UpdateLeadDto} from "../services/types/lead-generation-update.types";
import {LeadGenerationQuotePdfDto} from "../services/types/lead-generation-pdf-types";
import poll from "./utils/PollUtilities";
import {FilePondFile} from "filepond";

const myIzenApiService = new MyIzenApiService();
const myIzenFlowService = new MyIzenFlowService();

function isNotEmptyString( value: any )
{
    return typeof value === 'string' && value !== null && value !== undefined && value.trim() !== '';
}

export const LeadStore = types
    .model( 'LeadStore', {
        configuration: LeadConfigurationModel,
        currentStep: types.string,
        initialized: types.boolean,
        contact: FullContactModel,
        quote: QuoteModel,
        availablePackages: types.array( PackageModel ),
        solarSelection: SolarSelectionModel,
        options: LeadGenerationOptionsModel,
        quoteSimulation: QuoteSimulationModel,
        quoteBelfius: QuoteBelfiusModel,
        quotePdfGeneration: QuotePdfGenerationModel,
        notes: NoteModel
    } )
    .views( self => ({
        isContactInformationValid(): boolean
        {
            return isNotEmptyString( self.contact.firstName ) && isNotEmptyString( self.contact.lastName ) && isNotEmptyString(
                self.contact.email ) && isNotEmptyString( self.contact.street ) && isNotEmptyString( self.contact.number ) && isNotEmptyString(
                self.contact.postalCode ) && isNotEmptyString( self.contact.phoneNumber );
        },
        isForExistingBuilding()
        {
            return self.quote.homeStatus === 'existing';
        },
        isSolarBatterySelectionStepValid()
        {
            return self.solarSelection.roofMaterial != null && self.solarSelection.roofSlope != null && self.solarSelection.roofOrientation != null && self.solarSelection.roofLength != null && self.solarSelection.roofWidth != null;
        }
    }) )
    .views( self => ({
        translate( code: string ): string
        {
            if ( self.configuration.translations.has( code ) ) {
                return (self.configuration.translations.get( code ) as any).value;
            }
            return code;
        },
        canCreateAppointment(): boolean
        {
            return self.isContactInformationValid();
        },
        currentStepValid(): boolean
        {
            switch ( self.currentStep ) {
                case LeadGenerationStep.ORDER_TYPE:
                    return self.isContactInformationValid() && self.isForExistingBuilding() && self.quote.yearlyElectricityUsage != null;
                case LeadGenerationStep.SOLAR_BATTERY:
                    return self.isSolarBatterySelectionStepValid();
                case LeadGenerationStep.SOLAR:
                    return true;
                case LeadGenerationStep.BATTERY:
                    return true;
                case LeadGenerationStep.ORDER:
                    return true;
                default:
                    return true;
            }
        }
    }) )
    .actions( self => ({
        sendConfirmationMail: flow( function* () {
            yield myIzenApiService.sendConfirmationMail( self.contact.reference, self.quote.projectReference, self.configuration.locale );
        } )
    }) )
    .actions( self => ({
        setAvailablePackages( packages: any )
        {
            self.availablePackages = packages.map( ( pack: any ) => {
                return {
                    text: pack.label, value: pack.id, selected: self.quote.chosenPackage === pack.id
                }
            } );
        },
        setConfigurationOptions( configurationOptions: LeadGenerationOptions )
        {
            self.options.panel = configurationOptions.panel;
            self.options.battery = configurationOptions.battery;
            self.options.roof = {
                material: cast( configurationOptions.roof.material.options ),
                type: cast( configurationOptions.roof.type.options ),
                orientation: cast( configurationOptions.roof.orientation.options )
            };
        },
        setSimulation( simulation: any )
        {
            self.quoteSimulation = simulation;
        },
        setBelfiusQuote( belfiusQuote: any )
        {
            self.quoteBelfius = belfiusQuote;
        },
    }) )
    .actions( self => ({
        fetchAvailablePackages: flow( function* () {
            const fetched = yield myIzenApiService.getAvailablePackages( self.quote.homeStatus, self.contact.postalCode, self.configuration.locale );
            self.setAvailablePackages( fetched );
        } ),
        fetchConfigurationOptions: flow( function* () {
            const apiResponse = yield izenApiService.getConfigurationOptions( self.configuration.locale );
            self.setConfigurationOptions( apiResponse );
            self.quote.solarQuantity = apiResponse.panel.min;
            self.quote.batteryQuantity = apiResponse.battery.defaultCapacity;
            return !!apiResponse;
        } )
    }) )
    .actions( self => ({
        pollGeneratedPdf: flow( function* () {
            const stopCondition = ( blob: Blob ) => blob === null;
            const pdfIdentifier = self.quotePdfGeneration.pdfIdentifier as string;
            const functionToPoll = () => (myIzenApiService.getPdf( pdfIdentifier ));

            const response: PdfResponse = yield poll( functionToPoll, stopCondition, 2000, "Waiting to poll PDF again", 45 );
            const blob: Blob = yield response.blob;

            self.quotePdfGeneration.generationState = "Generated";
            self.quotePdfGeneration.blobUrl = window.URL.createObjectURL( blob );
            let link = document.createElement( "a" );
            link.href = window.URL.createObjectURL( blob );
            link.download = `${response.fileName}.pdf`;
            link.click();
            self.quotePdfGeneration.downloadState = "Downloaded";
        } ),
    }) )
    .actions( self => ({
        startPdfGeneration: flow( function* () {
            const leadGenerationPdfDto: LeadGenerationQuotePdfDto = {
                batteryAmount: self.quote.batteryQuantity as number,
                belfiusFinance: false, // TODO: where did we put this again
                contactKey: self.contact.reference,
                projectId: Number( self.quote.projectReference ),
                solarPanelsAmount: self.quote.solarQuantity as number
            }

            let pdfIdentifier = yield myIzenApiService.createPdf( leadGenerationPdfDto );
            if ( pdfIdentifier !== null ) {
                self.quotePdfGeneration.generationState = "Generating";
                self.quotePdfGeneration.pdfIdentifier = pdfIdentifier;
                yield self.pollGeneratedPdf();
            }
            else {
                toastr.error( self.translate( "leadgeneration.simulation.pdfDownloadFail" ) );
            }
        } )
    }) )
    .actions( self => ({
        uploadNotes: flow( function* ( files: FilePondFile[] ) {
            const formData = new FormData();
            const innerFiles: any = files.map( file => file.file as File );

            for ( let i = 0; i < innerFiles.length; i++ ) {
                formData.append( "files", innerFiles[i] )
            }

            yield myIzenApiService.addNotes( formData, self.contact.reference, self.quote.projectReference )
            self.notes.uploadedNoteFiles = true;
        } )
    }) )
    .actions( self => ({
        createLead: flow( function* () {
            let buildingTypeEnumElement = BuildingTypeEnum[self.quote.homeStatus.toUpperCase() as keyof typeof BuildingTypeEnum];
            let source = "";

            if ( buildingTypeEnumElement == BuildingTypeEnum.EXISTING ) {
                source = self.configuration.sourceExisting?.name ?? 'Regulier';
            }
            else if ( buildingTypeEnumElement == BuildingTypeEnum.NEW ) {
                source = self.configuration.sourceNew?.name ?? 'Regu Project Sales';
            }

            const createLead: CreateLeadDto = {
                buildingType: buildingTypeEnumElement,
                contactInformation: {
                    language: self.configuration.locale, //TODO test locale in store
                    email: self.contact.email,
                    firstName: self.contact.firstName,
                    lastName: self.contact.lastName,
                    street: self.contact.street,
                    houseNumber: self.contact.number,
                    mailbox: self.contact.numberAddition,
                    mobilePhone: self.contact.phoneNumber,
                    vatNumber: self.contact.vatNumber,
                    postalCode: self.contact.postalCode,
                    fullPostalCode: self.contact.fullPostalCode
                },
                source: source,
                type: TypeEnum[self.quote.chosenPackage as keyof typeof TypeEnum],
                yearlyEnergyConsumption: self.quote.yearlyElectricityUsage
            };

            const result = yield myIzenApiService.createLead( createLead );
            self.contact.reference = result.contact.toString();
            self.quote.projectReference = result.project.toString();
        } ),
        updateLead: flow( function* () {
            const updateLead: UpdateLeadDto = {
                contactInformation: {
                    // digitalCounter: true,
                    // existingClient: true,
                }, type: TypeEnum[self.quote.chosenPackage as keyof typeof TypeEnum],
                offer: {
                    battery: {
                        amount: self.quote.batteryQuantity,
                        // invertorBrand: null
                    }, solar: {
                        amount: self.quote.solarQuantity,
                        roof: {
                            // height: undefined,
                            length: self.solarSelection.roofLength,
                            material: self.solarSelection.roofMaterial,
                            orientation: self.solarSelection.roofOrientation,
                            slope: self.solarSelection.roofSlope,
                            surface: self.solarSelection.roofSurface,
                            width: self.solarSelection.roofWidth,
                        }
                    }
                }, yearlyEnergyConsumption: self.quote.yearlyElectricityUsage
            };

            yield myIzenApiService.updateLead( updateLead, self.contact.reference, self.quote.projectReference );
        } ),
        updateLeadForAppointment: flow( function* () {
            yield myIzenApiService.updateLead( {} as UpdateLeadDto, self.contact.reference, self.quote.projectReference );
        } ),

    }) )
    .actions( self => ({
        doSimulation: flow( function* () {
            const fetched = yield myIzenApiService.getSimulation( self.quote.solarQuantity || 1, self.quote.batteryQuantity || 0, self.configuration.locale );
            self.setSimulation( fetched );
            if ( self.quoteSimulation.reloading ) {
                self.quoteSimulation.reloading = false;
            }
            if ( self.configuration.supportsBelfiusFinancing ) {
                leadStore.fetchBelfiusQuote();
            }
        } )
    }) )
    .actions( self => ({
        fetchBelfiusQuote: flow( function* () {
            const fetched = yield myIzenApiService.getBelfiusQuote( self.quoteSimulation.total, self.configuration.locale );
            self.setBelfiusQuote( fetched );
            if ( self.quoteBelfius.reloading ) {
                self.quoteBelfius.reloading = false;
            }
        } )
    }) )
    .actions( self => ({
        initialize( configuration: any )
        {

            Object.entries( configuration.translations )
                .map( ( entry: any[] ) => {
                    return {key: entry[0], value: entry[1]}
                } )
                .forEach( obj => self.configuration.translations.put( obj ) );
            self.configuration.invalidDomains = configuration.invalidDomains;
            self.configuration.showAppointmentButton = configuration.showAppointmentButton;
            self.configuration.appointmentMessage = configuration.appointmentMessage;
            self.configuration.stepOneNextButtonText = configuration.stepOneNextButtonText;
            self.configuration.stepOneAppointmentButtonText = configuration.stepOneAppointmentButtonText;
            self.configuration.sourceExisting = configuration.sourceExisting;
            self.configuration.sourceNew = configuration.sourceNew;
            self.configuration.source = configuration.source;
            self.configuration.theme = configuration.theme;
            self.configuration.locale = configuration.locale;
            self.configuration.supportsBelfiusFinancing = configuration.supportsBelfiusFinancing;
            self.initialized = true;
            self.currentStep = LeadGenerationStep.ORDER_TYPE;
            self.fetchAvailablePackages();
        },
        goToNextStep: flow( function* () {
            if ( self.currentStep === LeadGenerationStep.ORDER_TYPE ) {
                yield self.createLead();
            }
            if ( self.currentStep === LeadGenerationStep.CREATE_APPOINTMENT ) {
                yield self.updateLeadForAppointment();
            }
            self.currentStep = myIzenFlowService.getNextStep( self.currentStep, self.quote );
        } ),
        createAppointment: flow( function* () {
            if ( self.currentStep === LeadGenerationStep.ORDER_TYPE ) {
                yield self.createLead();
                self.sendConfirmationMail();
                if ( self.quote.homeStatus === "new" ) {
                    self.currentStep = LeadGenerationStep.CONFIRMATION_NEW_CONSTRUCTION;
                }
                else {
                    self.currentStep = LeadGenerationStep.CREATE_APPOINTMENT;
                }
            }
        } ),
        changeStep( step: string )
        {
            self.currentStep = step;
        },
        setFirstName( value: string )
        {
            self.contact.firstName = value;
        },
        setLastName( value: string )
        {
            self.contact.lastName = value;
        },
        setEmail( value: string )
        {
            self.contact.email = value;
        },
        setStreet( value: string )
        {
            self.contact.street = value;
        },
        setNumber( value: string )
        {
            self.contact.number = value;
        },
        setNumberAddition( value: string )
        {
            self.contact.numberAddition = value;
        },
        setPostalCode( simpleValue: string, fullValue: string )
        {
            self.contact.postalCode = simpleValue;
            self.contact.fullPostalCode = fullValue;

            self.fetchAvailablePackages();
        },
        setPhoneNumber( value: string )
        {
            self.contact.phoneNumber = value;
        },
        setVatNumber( value: string )
        {
            self.contact.vatNumber = value;
        },
        setHomeStatus( value: string )
        {
            self.quote.homeStatus = value;
            self.fetchAvailablePackages();
        },
        setChosenPackage( value: string )
        {
            self.quote.chosenPackage = value;
        },
        setYearlyElectricityUsage( value: number )
        {
            self.quote.yearlyElectricityUsage = value;
        },
        setSolarQuantity( value: number )
        {
            self.quoteSimulation.reloading = true;
            self.quoteBelfius.reloading = true;
            self.quote.solarQuantity = value;
            redoSimulation();
        },
        setBatteryQuantity( value: number )
        {
            self.quote.batteryQuantity = value;
            self.quoteSimulation.reloading = true;
            self.quoteBelfius.reloading = true;
            self.doSimulation();
        }
    }) );

// singleton usage
let initialState = {
    configuration: {
        locale: 'nl',
        theme: "DEFAULT",
        appointmentMessage: "Beste,\nWe hebben je aanvraag goed ontvangen. Je hebt van ons een mail ontvangen met een persoonlijke code en een link om een afspraak in te plannen.\n" +
                "Gebruik je persoonlijke code en plan een afspraak in.\n" +
                "Mocht je de mail niet onmiddellijk vinden, bekijk dan zeker ook je spam-folder.\n\nHeb je meer vragen, dan horen we het graag.\n" +
                "Je kan ons altijd bereiken op 056 98 05 10 (Earth West - HQ) of 014 55 83 19 (Earth Oost).\n" +
                "Of je kan ons ook altijd een mailtje sturen op info@earth.be.\n" +
                "\n" +
                "Vriendelijke groeten,\n" +
                "\n" +
                "Team Earth",
        stepOneNextButtonText: "BEREKEN JE OFFERTE",
        stepOneAppointmentButtonText: "MAAK EEN AFSPRAAK",
        translations: {}
    },
    currentStep: LeadGenerationStep.ORDER_TYPE,
    initialized: false,
    contact: {
        firstName: '',
        lastName: '',
        email: '',
        street: '',
        postalCode: '',
        fullPostalCode: '',
        number: '',
        numberAddition: '',
        phoneNumber: '',
        vatNumber: '',
    },
    quote: {
        homeStatus: 'existing',
        chosenPackage: 'solar+battery',
        yearlyElectricityUsage: undefined
    },
    availablePackages: [],
    solarSelection: SolarSelectionModel.create(),
    options: LeadGenerationOptionsModel.create(),
    quoteSimulation: {},
    quoteBelfius: {},
    quotePdfGeneration: {
        generationState: "NotYetGenerated",
        downloadState: "NotDownloaded"
    },
    notes: {
        uploadedNoteFiles: false
    }

}

if ( !process.env.NODE_ENV || process.env.NODE_ENV === 'development' ) {
    console.log( "we in dev mode now" )
    if ( localStorage.getItem( "leadgeneration-state" ) ) {
        initialState = JSON.parse( localStorage.getItem( "leadgeneration-state" ) as string )
    }
}

const leadStore = LeadStore.create( initialState );

if ( !process.env.NODE_ENV || process.env.NODE_ENV === 'development' ) {
    onSnapshot( leadStore, snapshot => {
        localStorage.setItem( "leadgeneration-state", JSON.stringify( snapshot ) )
    } )
}

makeInspectable( leadStore );
const LeadContext = React.createContext( leadStore );

const redoSimulation = debounce( leadStore.doSimulation, 200 );

export function useLeadStore()
{
    const store = React.useContext( LeadContext );
    return store;
}
